import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast, Toaster } from "sonner";
import Navbar from "@/components/Navbar";
import StatusBadge from "@/components/StatusBadge";
import {
  ShieldCheck, Star, Clock, MapPin, AlertTriangle, CheckCircle2, MessageSquare,
} from "lucide-react";

/**
 * MyRequest — customer's view of one service request and every provider
 * quote received so far. Refreshes every 8s until the customer has picked a
 * provider, so new quotes appear live without a page reload.
 *
 * Provider contact details are NEVER shown here — they are released to the
 * customer only after the customer completes payment via the existing
 * /bookings/{id} → Stripe Checkout flow.
 */
export default function MyRequest() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [selecting, setSelecting] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/leads/${id}`);
      setData(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not load request");
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [id]);

  if (!data) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 py-10 text-slate-500">Loading your request…</div>
      </div>
    );
  }

  const lead = data.lead || {};
  const quotes = data.quotes || [];
  const selected = quotes.find((q) => q.status === "selected");
  const canSelect = !selected && ["new","notified","sent_to_providers","quotes_received"].includes(lead.status);

  const pick = async (quoteId) => {
    setSelecting(quoteId);
    try {
      const { data: r } = await api.post(`/leads/${id}/select-provider`, { quote_id: quoteId });
      toast.success("Provider selected — proceed to payment to confirm the booking");
      nav(`/bookings/${r.booking_id}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not select provider");
    } finally { setSelecting(null); }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <Toaster position="top-center" />
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex flex-wrap items-center gap-3">
          {lead.urgent && (
            <span data-testid="mr-urgent-badge" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-rose-600 text-white text-xs font-bold">
              <AlertTriangle size={12}/> URGENT
            </span>
          )}
          <span className="text-xs text-slate-500">Ref: <b>{lead.ref}</b></span>
          <StatusBadge status={lead.status} />
        </div>
        <h1 className="font-display text-3xl font-bold mt-2 text-slate-900">Your request</h1>
        <div className="mt-3 card">
          <div className="text-sm text-slate-600 flex flex-wrap gap-x-4 gap-y-1">
            <span><b>Service:</b> {lead.category}</span>
            <span><b>Postcode:</b> {lead.postcode}</span>
            <span><b>When:</b> {lead.when_needed}</span>
          </div>
          <p className="mt-2 text-slate-800">{lead.description}</p>
        </div>

        <div className="mt-8 flex items-baseline justify-between">
          <h2 className="font-display text-2xl font-bold">Quotes received</h2>
          <div className="text-xs text-slate-500">
            <span data-testid="mr-quotes-count">{data.quotes_received}</span> of{" "}
            <span data-testid="mr-notified-count">{data.providers_notified}</span> providers responded
          </div>
        </div>

        {quotes.length === 0 && (
          <div className="mt-4 card text-slate-600 text-sm">
            <div className="flex items-start gap-2">
              <Clock size={16} className="text-emerald-600 mt-0.5"/>
              <div>
                <b>Waiting for local pros to respond.</b> Verified FixNear providers in your area have been notified — quotes will appear on this page automatically. Typical response: under 20 minutes for urgent jobs.
              </div>
            </div>
          </div>
        )}

        <div className="mt-4 space-y-3">
          {quotes.map((q) => {
            const isSelected = q.status === "selected";
            const isNotSelected = q.status === "not_selected";
            return (
              <div key={q.id} data-testid={`mr-quote-${q.id}`}
                className={`rounded-2xl border p-4 ${isSelected ? "border-emerald-400 bg-emerald-50/40" : isNotSelected ? "border-slate-200 bg-slate-50 opacity-70" : "border-slate-200 bg-white"}`}>
                <div className="flex items-start gap-4">
                  <div className="shrink-0">
                    {q.provider_photo ? (
                      <img src={q.provider_photo} alt="" className="w-14 h-14 rounded-2xl object-cover" />
                    ) : (
                      <div className="w-14 h-14 rounded-2xl bg-emerald-100 text-emerald-800 font-bold text-lg flex items-center justify-center">
                        {(q.provider_name || "?").split(" ").map((s)=>s[0]).slice(0,2).join("")}
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="font-bold text-slate-900 truncate">{q.provider_name || "Local pro"}</div>
                      {q.provider_verified && (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-700 font-semibold"><ShieldCheck size={12}/> Verified</span>
                      )}
                      {typeof q.provider_rating === "number" && q.provider_rating > 0 && (
                        <span className="inline-flex items-center gap-1 text-xs text-amber-700">
                          <Star size={12} className="fill-amber-500 text-amber-500"/> {q.provider_rating.toFixed(1)}
                          <span className="text-slate-500">({q.provider_review_count})</span>
                        </span>
                      )}
                      {q.distance_hint && (
                        <span className="inline-flex items-center gap-1 text-xs text-slate-500"><MapPin size={12}/> {q.distance_hint}</span>
                      )}
                    </div>
                    {q.quote_message && (
                      <div className="mt-2 text-sm text-slate-700 flex items-start gap-2">
                        <MessageSquare size={14} className="mt-0.5 text-slate-400 shrink-0"/>
                        <div className="line-clamp-3">{q.quote_message}</div>
                      </div>
                    )}
                    <div className="mt-3 flex flex-wrap items-center gap-4">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-500">Quoted price</div>
                        <div className="text-2xl font-display font-bold text-slate-900">£{(q.quote_amount || 0).toFixed(2)}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-500">Est. arrival</div>
                        <div className="text-sm font-semibold text-slate-800">
                          {q.quote_eta_minutes ? `~ ${q.quote_eta_minutes} min` : "Provider to confirm"}
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
                      {isSelected ? (
                        <div className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700">
                          <CheckCircle2 size={16}/> Selected — complete payment to confirm
                        </div>
                      ) : isNotSelected ? (
                        <div className="text-xs text-slate-500 italic">Not selected</div>
                      ) : canSelect ? (
                        <button
                          data-testid={`mr-select-${q.id}`}
                          disabled={selecting === q.id}
                          onClick={() => pick(q.id)}
                          className="btn-accent"
                        >{selecting === q.id ? "Selecting…" : "Select Provider"}</button>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {selected && (
          <div className="mt-6 card border-emerald-300 bg-emerald-50/30">
            <div className="font-bold text-emerald-900">Next step</div>
            <p className="text-sm text-emerald-900/90 mt-1">
              You've picked <b>{selected.provider_name}</b>. Complete payment to release contact details and confirm your booking.
              FixNear takes {(lead.commission_pct || 15)}% platform commission; the rest goes to your provider via Stripe Connect.
            </p>
            <div className="mt-3">
              <Link to="/customer" className="btn-outline text-sm">View my requests</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
